import { SurveyEngine } from './engine/survey-engine.js';
import { launchSurvey } from './engine/survey-launcher.js';
import {
    getAttributes,
    getLoadedProjectConfig,
    getStorage,
    handleCookieAndLocalStorage,
    isStandAlonePreview,
} from './initialize.js';
import { checkShowConditions, getSurveyDisplayDecision, isSurveyActive } from './display/conditions.js';
import { checkURL } from './display/rules.js';
import { CONFIG, surveySettingsDefaults } from './config.js';
import { fetchSurveyPreview } from './api/backend.js';
import { normalizeCan } from './utils/normalize.js';

export class PollHero extends HTMLElement {
    static get observedAttributes() {
        return ['surveydata', 'currentindex', 'can'];
    }

    constructor() {
        super();
        this.surveyId = this.getAttribute('survey-id');
        this.surveyData = null;
        this.surveyType = this.getAttribute('surveyType') || 'embed';
        this.currentIndex = 0;
        this.surveyEngineInstance = null;
        this.can = {};
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'surveydata' && this.surveyEngineInstance) {
            this.surveyData = JSON.parse(newValue);
            this.render();
        }

        if (name === 'currentindex' && this.surveyEngineInstance) {
            this.currentIndex = parseInt(newValue, 10) || 0;
            this.render();
        }

        if (name === 'can') {
            try {
                this.can = JSON.parse(newValue) || {};
            } catch {
                this.can = {};
            }
        }
    }

    render() {
        if (this.parentNode) {
            this.surveyEngineInstance.updateSurveyData(this.surveyData, this.currentIndex);
        }
    }

    connectedCallback() {
        this.surveyId = this.getAttribute('survey-id');
        this.isPreview = this.getAttribute('isPreview') ?? false;
        this.surveyData = this.getAttribute('surveyData')
            ? JSON.parse(this.getAttribute('surveyData'))
            : null;
        this.surveyType = this.getAttribute('surveyType') || 'embed';
        this.currentIndex = parseInt(this.getAttribute('currentIndex'), 10) || 0;

        const canAttr = this.getAttribute('can');
        if (canAttr) {
            try {
                this.can = JSON.parse(canAttr) || {};
            } catch {
                this.can = {};
            }
        }

        if (this.surveyId && this.surveyType === 'standalone' && isStandAlonePreview()) {
            // Script loaded with isStandAlone=true&isPreview=true
            this.renderStandalonePreview();
            return;
        }

        if (this.surveyId && (this.surveyType === 'embed' || this.surveyType === 'standalone')) {
            const loadedProjectConfig = getLoadedProjectConfig();
            if (loadedProjectConfig) {
                const surveyFromConfig = loadedProjectConfig.surveys?.find(
                    (s) => s.survey_id === this.surveyId,
                );
                if (surveyFromConfig && isSurveyActive(surveyFromConfig)) {
                    const can = Object.keys(this.can).length > 0 ? this.can : (loadedProjectConfig.can || {});
                
                    let storage = getStorage();
                    if (storage === null && (this.surveyType === 'embed' || this.surveyType === 'standalone')) {
                        handleCookieAndLocalStorage(
                            loadedProjectConfig.storage_type,
                            loadedProjectConfig.cookies_expiration_time,
                        );
                        storage = getStorage();
                    }
                    if (this.surveyType === 'embed') {
                        const surveySettings = surveyFromConfig.survey_settings || surveySettingsDefaults;
                        if (!getSurveyDisplayDecision(surveyFromConfig, storage)) return;
                        if (!checkURL(surveyFromConfig.rules, surveyFromConfig.survey_settings?.urlRulesLogicOperator, getAttributes())) return;
                        if (!checkShowConditions(surveySettings, this.surveyId, storage)) return;
                        launchSurvey(surveyFromConfig, this.surveyType, storage, can, this);
                    }
                    if (this.surveyType === 'standalone') {
                        // Sampling ("% of users") applies to standalone pages too;
                        // a user outside the sample gets no survey on this page
                        if (!getSurveyDisplayDecision(surveyFromConfig, storage)) return;
                        new SurveyEngine(surveyFromConfig, this.surveyType, this.currentIndex, null, storage, can);
                    }

                }
            }
        }

        if (!this.surveyId && this.surveyType === 'preview' && this.surveyData && this.parentNode) {
            const previewCan = Object.keys(this.can).length > 0 ? this.can : (this.surveyData.can || {});
            // forceexpanded: the question preview tab always shows the survey
            // opened, even when the style says it starts minimized
            // staticlayout: the box stays in normal flow (the embedding
            // component places it) instead of anchoring to the pane
            // previewtype: which survey type the preview mimics (e.g. 'embed')
            const forceExpanded = this.readBooleanAttribute('forceexpanded');
            const staticLayout = this.readBooleanAttribute('staticlayout');
            const previewType = this.getAttribute('previewtype') || null;
            this.surveyEngineInstance = new SurveyEngine(
                this.surveyData,
                'preview',
                this.currentIndex,
                this.parentNode,
                null,
                previewCan,
                { forceExpanded, staticLayout, previewType },
            );
        }
    }

    // Standalone preview: the survey (including unpublished drafts) is fetched
    // from the server like the link preview. The project config is only a
    // fallback. Always rendered, nothing stored, nothing sent to the backend.
    async renderStandalonePreview() {
        let survey = null;
        try {
            survey = await fetchSurveyPreview(CONFIG.apiUrl, this.surveyId);
        } catch {
            survey = getLoadedProjectConfig()?.surveys?.find(
                (s) => s.survey_id === this.surveyId,
            ) ?? null;
        }
        if (!survey || !this.isConnected) return;

        const can = Object.keys(this.can).length > 0
            ? this.can
            : normalizeCan(survey.can || getLoadedProjectConfig()?.can || {});

        this.surveyEngineInstance = new SurveyEngine(survey, 'previewStandalone', this.currentIndex, null, null, can);
    }

    // Boolean attributes are true when present, unless explicitly "false"/"0"
    readBooleanAttribute(name) {
        const value = this.getAttribute(name);
        return this.hasAttribute(name) && value !== 'false' && value !== '0';
    }
}
